import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility, MdPrint } from 'react-icons/md';
import '../master/MasterData.css';

interface Vendor {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  vendorType: string;
  tin: string;
  email: string;
  phone: string;
  city: string;
  isActive: string;
}

interface InvoiceItem {
  itemId: number;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  vatAmount: number;
}

interface PurchaseInvoice {
  invoiceId: number;
  invoiceNo: string;
  invoiceDate: string;
  receiptNo: string;
  vendorCode: string;
  vendorName: string;
  vendorTIN: string;
  dueDate: string;
  subtotal: number;
  vatAmount: number;
  totalAmount: number;
  status: 'DRAFT' | 'POSTED' | 'APPROVED' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  items: InvoiceItem[];
  terms: string;
  remarks: string;
}

interface InvoiceModalProps {
  invoice: PurchaseInvoice | null;
  viewMode: boolean;
  vendors: Vendor[];
  onClose: () => void;
  onSave: (invoice: PurchaseInvoice) => void;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({ invoice, viewMode, vendors, onClose, onSave }) => {
  const [formData, setFormData] = useState<PurchaseInvoice>(
    invoice || {
      invoiceId: 0,
      invoiceNo: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      receiptNo: '',
      vendorCode: '',
      vendorName: '',
      vendorTIN: '',
      dueDate: '',
      subtotal: 0,
      vatAmount: 0,
      totalAmount: 0,
      status: 'DRAFT',
      items: [],
      terms: 'Net 30',
      remarks: ''
    }
  );

  useEffect(() => {
    calculateTotals();
  }, [formData.items]);

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.amount, 0);
    const vatAmount = formData.items.reduce((sum, item) => sum + item.vatAmount, 0);
    const totalAmount = subtotal + vatAmount;

    setFormData(prev => ({
      ...prev,
      subtotal,
      vatAmount,
      totalAmount
    }));
  };

  const handleVendorChange = (vendorCode: string) => {
    const selectedVendor = vendors.find(v => v.vendorCode === vendorCode);
    if (selectedVendor) {
      setFormData({
        ...formData,
        vendorCode: selectedVendor.vendorCode,
        vendorName: selectedVendor.vendorName,
        vendorTIN: selectedVendor.tin
      });
    }
  };

  const handleAddItem = () => {
    const newItem: InvoiceItem = {
      itemId: Date.now(),
      itemCode: '',
      itemName: '',
      quantity: 0,
      unitPrice: 0,
      amount: 0,
      vatAmount: 0
    };
    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });
  };

  const handleRemoveItem = (itemId: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter(item => item.itemId !== itemId)
    });
  };

  const handleItemChange = (itemId: number, field: keyof InvoiceItem, value: any) => {
    const updatedItems = formData.items.map(item => {
      if (item.itemId === itemId) {
        const updatedItem = { ...item, [field]: value };

        // Recalculate amount and VAT when quantity or unitPrice changes
        if (field === 'quantity' || field === 'unitPrice') {
          const amount = updatedItem.quantity * updatedItem.unitPrice;
          const vatAmount = amount * 0.12; // 12% VAT
          return { ...updatedItem, amount, vatAmount };
        }

        return updatedItem;
      }
      return item;
    });

    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewMode) {
      onSave(formData);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{viewMode ? 'View Purchase Invoice' : invoice ? 'Edit Purchase Invoice' : 'Record New Purchase Invoice'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Invoice No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.invoiceNo}
                  onChange={(e) => setFormData({ ...formData, invoiceNo: e.target.value })}
                  placeholder="PI-2025-XXXX"
                />
              </div>
              <div className="form-group">
                <label>Invoice Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.invoiceDate}
                  onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Due Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vendor Name *</label>
                <select
                  required
                  disabled={viewMode}
                  value={formData.vendorCode}
                  onChange={(e) => handleVendorChange(e.target.value)}
                >
                  <option value="">Select Vendor</option>
                  {vendors.filter(v => v.isActive === 'Y').map(vendor => (
                    <option key={vendor.vendorId} value={vendor.vendorCode}>
                      {vendor.vendorName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Vendor TIN</label>
                <input
                  type="text"
                  disabled
                  value={formData.vendorTIN}
                  placeholder="Auto-filled from vendor"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Receipt No</label>
                <input
                  type="text"
                  disabled={viewMode}
                  value={formData.receiptNo}
                  onChange={(e) => setFormData({ ...formData, receiptNo: e.target.value })}
                  placeholder="GR-2025-XXXX"
                />
              </div>
              <div className="form-group">
                <label>Payment Terms *</label>
                <select
                  required
                  disabled={viewMode}
                  value={formData.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                >
                  <option value="Net 15">Net 15</option>
                  <option value="Net 30">Net 30</option>
                  <option value="Net 45">Net 45</option>
                  <option value="Net 60">Net 60</option>
                  <option value="COD">Cash on Delivery</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  disabled={viewMode}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="DRAFT">Draft</option>
                  <option value="POSTED">Posted</option>
                  <option value="APPROVED">Approved</option>
                  <option value="PAID">Paid</option>
                  <option value="OVERDUE">Overdue</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h4>Items</h4>
                {!viewMode && (
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleAddItem}>
                    <MdAdd /> Add Item
                  </button>
                )}
              </div>

              <table className="item-table">
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Amount</th>
                    {!viewMode && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map(item => (
                    <tr key={item.itemId}>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemCode}
                          onChange={(e) => handleItemChange(item.itemId, 'itemCode', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemName}
                          onChange={(e) => handleItemChange(item.itemId, 'itemName', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.quantity}
                          onChange={(e) => handleItemChange(item.itemId, 'quantity', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(item.itemId, 'unitPrice', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>₱{item.amount.toLocaleString()}</td>
                      {!viewMode && (
                        <td>
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => handleRemoveItem(item.itemId)}
                          >
                            <MdDelete />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>Subtotal:</td>
                    <td style={{ fontWeight: 'bold' }}>₱{formData.subtotal.toLocaleString()}</td>
                    {!viewMode && <td></td>}
                  </tr>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>VAT (12%):</td>
                    <td style={{ fontWeight: 'bold' }}>₱{formData.vatAmount.toLocaleString()}</td>
                    {!viewMode && <td></td>}
                  </tr>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold', fontSize: '16px' }}>Total Amount:</td>
                    <td style={{ fontWeight: 'bold', fontSize: '16px' }}>₱{formData.totalAmount.toLocaleString()}</td>
                    {!viewMode && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                disabled={viewMode}
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={3}
                placeholder="Additional notes about this invoice..."
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {viewMode ? 'Close' : 'Cancel'}
            </button>
            {!viewMode && (
              <button type="submit" className="btn btn-primary">
                {invoice ? 'Update' : 'Record'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const PurchaseInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<PurchaseInvoice[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<PurchaseInvoice | null>(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    // TODO: Fetch vendors from API
    setVendors([
      {
        vendorId: 1,
        vendorCode: 'VEND001',
        vendorName: 'Foam Suppliers Inc.',
        vendorType: 'MANUFACTURER',
        tin: '111-222-333-000',
        email: 'foam@suppliers.com',
        phone: '+63 2 1111 2222',
        city: 'Laguna',
        isActive: 'Y'
      },
      {
        vendorId: 2,
        vendorCode: 'VEND002',
        vendorName: 'Chemical Trading Co.',
        vendorType: 'DISTRIBUTOR',
        tin: '444-555-666-000',
        email: 'chem@trading.com',
        phone: '+63 2 3333 4444',
        city: 'Batangas',
        isActive: 'Y'
      },
      {
        vendorId: 3,
        vendorCode: 'VEND003',
        vendorName: 'Packaging Materials Ltd.',
        vendorType: 'SUPPLIER',
        tin: '777-888-999-000',
        email: 'packaging@materials.com',
        phone: '+63 2 5555 6666',
        city: 'Manila',
        isActive: 'Y'
      }
    ]);

    // TODO: Fetch purchase invoices from API
    setInvoices([
      {
        invoiceId: 1,
        invoiceNo: 'PI-2025-0001',
        invoiceDate: '2025-02-15',
        receiptNo: 'GR-2025-0001',
        vendorCode: 'VEND001',
        vendorName: 'Foam Suppliers Inc.',
        vendorTIN: '111-222-333-000',
        dueDate: '2025-03-15',
        subtotal: 892857.14,
        vatAmount: 107142.86,
        totalAmount: 1000000,
        status: 'APPROVED',
        items: [],
        terms: 'Net 30',
        remarks: 'Bulk order payment'
      },
      {
        invoiceId: 2,
        invoiceNo: 'PI-2025-0002',
        invoiceDate: '2025-02-20',
        receiptNo: 'GR-2025-0002',
        vendorCode: 'VEND002',
        vendorName: 'Chemical Trading Co.',
        vendorTIN: '444-555-666-000',
        dueDate: '2025-04-05',
        subtotal: 446428.57,
        vatAmount: 53571.43,
        totalAmount: 500000,
        status: 'POSTED',
        items: [],
        terms: 'Net 45',
        remarks: ''
      },
      {
        invoiceId: 3,
        invoiceNo: 'PI-2025-0003',
        invoiceDate: '2025-01-10',
        receiptNo: 'GR-2025-0003',
        vendorCode: 'VEND003',
        vendorName: 'Packaging Materials Ltd.',
        vendorTIN: '777-888-999-000',
        dueDate: '2025-02-10',
        subtotal: 267857.14,
        vatAmount: 32142.86,
        totalAmount: 300000,
        status: 'OVERDUE',
        items: [],
        terms: 'Net 30',
        remarks: 'Payment overdue'
      }
    ]);
  }, []);

  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.vendorName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingInvoice(null);
    setViewMode(false);
    setShowModal(true);
  };

  const handleEdit = (invoice: PurchaseInvoice) => {
    setEditingInvoice(invoice);
    setViewMode(false);
    setShowModal(true);
  };

  const handleView = (invoice: PurchaseInvoice) => {
    setEditingInvoice(invoice);
    setViewMode(true);
    setShowModal(true);
  };

  const handleDelete = (invoiceId: number) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      setInvoices(invoices.filter(i => i.invoiceId !== invoiceId));
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'PAID': return 'badge-success';
      case 'APPROVED': return 'badge-info';
      case 'POSTED': return 'badge-primary';
      case 'OVERDUE': return 'badge-danger';
      case 'CANCELLED': return 'badge-secondary';
      default: return 'badge-warning';
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Purchase Invoices</h2>
            <p className="subtitle">Manage vendor tax invoices and payables</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Record New Invoice
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice No</th>
                  <th>Invoice Date</th>
                  <th>Vendor</th>
                  <th>Due Date</th>
                  <th>Subtotal</th>
                  <th>VAT (12%)</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(invoice => (
                  <tr key={invoice.invoiceId}>
                    <td className="code">{invoice.invoiceNo}</td>
                    <td>{new Date(invoice.invoiceDate).toLocaleDateString('en-US')}</td>
                    <td>{invoice.vendorName}</td>
                    <td>{new Date(invoice.dueDate).toLocaleDateString('en-US')}</td>
                    <td className="amount">₱{invoice.subtotal.toLocaleString()}</td>
                    <td className="amount">₱{invoice.vatAmount.toLocaleString()}</td>
                    <td className="amount">₱{invoice.totalAmount.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(invoice)}
                          title="View"
                        >
                          <MdVisibility />
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(invoice)}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn-icon btn-print"
                          onClick={() => window.print()}
                          title="Print"
                        >
                          <MdPrint />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(invoice.invoiceId)}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredInvoices.length === 0 && (
              <div className="no-data">
                <p>No invoices found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <InvoiceModal
          invoice={editingInvoice}
          viewMode={viewMode}
          vendors={vendors}
          onClose={() => setShowModal(false)}
          onSave={(invoice) => {
            if (editingInvoice) {
              setInvoices(invoices.map(i =>
                i.invoiceId === invoice.invoiceId ? invoice : i
              ));
            } else {
              setInvoices([...invoices, { ...invoice, invoiceId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

export default PurchaseInvoices;
